import { Head, useForm } from '@inertiajs/react'
import { useEffect, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { adminScriptImports } from '@/routes'
import type { Production } from '@/types'

type Preview = {
  characters: string[]
  scenes: { act: string | null; title: string; lines: { kind: string; character: string | null; text: string }[] }[]
}

const PLACEHOLDER = `ACT I

SCENE 1. A room in Capulet's house.

(Morning. JULIET enters.)

JULIET: Good morrow, Nurse.
LADY CAPULET: Your mother calls for you.

— or Fountain —

JULIET
Nurse, is he married?`

export default function NewScriptImport({ production }: { production: Production }) {
  const form = useForm({ text: '' })
  const [preview, setPreview] = useState<Preview | null>(null)

  // Re-parse as staff edit, so speaker and scene detection problems show up before import.
  useEffect(() => {
    if (!form.data.text.trim()) return setPreview(null)
    const controller = new AbortController()
    const timer = setTimeout(async () => {
      const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
      const route = adminScriptImports.preview({ productionId: production.slug })
      const res = await fetch(route.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        body: JSON.stringify({ text: form.data.text }),
        signal: controller.signal,
      }).catch(() => null)
      if (res?.ok) setPreview(await res.json())
    }, 400)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [form.data.text, production.slug])

  const lineCount = preview?.scenes.reduce((n, s) => n + s.lines.length, 0) ?? 0

  return (
    <div className="grid gap-6">
      <Head title={`Import script · ${production.title}`} />
      <div>
        <h1 className="text-2xl font-semibold">Import script</h1>
        <p className="text-muted-foreground">
          Paste play text or Fountain. Speakers in CAPS, followed by a colon or period, or on their own line.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <form
          className="grid content-start gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            form.submit(adminScriptImports.create({ productionId: production.slug }))
          }}
        >
          <Textarea
            className="min-h-[28rem] font-mono text-sm"
            value={form.data.text}
            onChange={(e) => form.setData('text', e.target.value)}
            placeholder={PLACEHOLDER}
          />
          <Button type="submit" disabled={form.processing || lineCount === 0} className="justify-self-start">
            Import {lineCount > 0 && `${lineCount} lines`}
          </Button>
        </form>

        <Card>
          <CardHeader>
            <CardTitle>Preview</CardTitle>
            <CardDescription>
              {preview ? (
                <span className="flex flex-wrap gap-1">
                  {preview.characters.map((c) => (
                    <Badge key={c} variant="secondary">
                      {c}
                    </Badge>
                  ))}
                </span>
              ) : (
                'Detected characters and scenes appear here.'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid max-h-[28rem] gap-4 overflow-y-auto">
            {preview?.scenes.map((scene, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: preview scenes have no ids
              <div key={`${scene.title}-${i}`} className="grid gap-1.5">
                <p className="text-xs font-semibold uppercase text-muted-foreground">
                  {[...new Set([scene.act, scene.title])].filter(Boolean).join(' · ')}
                </p>
                {scene.lines.map((line, j) =>
                  line.kind === 'direction' ? (
                    // biome-ignore lint/suspicious/noArrayIndexKey: preview lines have no ids
                    <p key={j} className="text-sm italic text-muted-foreground">
                      ({line.text})
                    </p>
                  ) : (
                    // biome-ignore lint/suspicious/noArrayIndexKey: preview lines have no ids
                    <p key={j} className="whitespace-pre-line text-sm">
                      <span className="font-semibold">{line.character}</span> {line.text}
                    </p>
                  ),
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
